/**
 * Atlas PM · UI Store
 *
 * 视图联动的中枢：
 *   地图点 → setCurrentProjectId → 星图读它
 *   星图点节点 → openTask(id) → 详情抽屉读它（drawerOpen）
 *   地图筛选 → filters / setFilter / resetFilters
 *
 * ⭐ p3-map / p3-star / p3-detail 都消费本 store，字段名不要改。
 */

import { create } from 'zustand';

/** 地图筛选条件（空值表示不筛选） */
export interface MapFilters {
  /** 健康等级：HEALTHY / WARNING / ALERT */
  healthLevel?: string;
  /** 项目类型 */
  projectType?: string;
  /** 任务形态（TASK_FORM） */
  taskForm?: string;
  /** 下钻到的省（行政区划码，如 330000） */
  provinceCode?: string;
}

/** 星图页注册到顶栏面包屑的项目信息（P5.4：第三级「项目名」） */
export interface CurrentProjectMeta {
  /** 项目名 */
  name: string | null;
  /** 所属省行政区划码（供面包屑「浙江省」点击回省份视图） */
  provinceCode: string | null;
  /** 所属省名 */
  provinceName: string | null;
}

const EMPTY_PROJECT_META: CurrentProjectMeta = {
  name: null,
  provinceCode: null,
  provinceName: null,
};

interface UiState {
  /** 当前选中的项目 id（从地图进入星图时设置） */
  currentProjectId: string | null;
  setCurrentProjectId(id: string | null): void;

  /**
   * 当前项目元信息（星图页加载项目后写入，供 AppShell 顶栏面包屑拼接第三级）。
   * ⚠️ 与 currentProjectId 分离：id 由地图点击同步写入，元信息需等接口返回。
   */
  currentProjectMeta: CurrentProjectMeta;
  setCurrentProjectMeta(meta: Partial<CurrentProjectMeta>): void;
  clearCurrentProjectMeta(): void;

  /** 当前打开的任务 id（打开抽屉时设置） */
  currentTaskId: string | null;
  openTask(id: string): void;
  closeTask(): void;

  /** 地图筛选条件 */
  filters: MapFilters;
  /** key 必须属于 MapFilters；value 为 undefined 时移除该条件 */
  setFilter(key: keyof MapFilters, value: string | undefined): void;
  resetFilters(): void;

  /** 任务详情抽屉是否展开 */
  drawerOpen: boolean;

  /**
   * P6.2 · 新建任务抽屉（与详情抽屉同一种浮层方式，不再跳新页面）。
   * ⚠️ 与 currentTaskId / drawerOpen 并存、互斥：任一抽屉打开会关闭另一个。
   */
  taskFormOpen: boolean;
  /** 新建任务要挂到哪个项目下（打开时可带） */
  taskFormProjectId: string | null;
  openTaskForm(projectId?: string): void;
  closeTaskForm(): void;

  /**
   * ⭐ P7.5 · 编辑任务模式：任务设计器复用同一抽屉，此字段非空即进入编辑态。
   *    null / undefined = 新建；有值 = 编辑对应任务。
   *    ⚠️ 与 taskFormProjectId 互斥语义：编辑时以 taskFormEditId 为准。
   */
  taskFormEditId: string | null;
  /** 打开「编辑任务」抽屉（从任务详情底部操作区「编辑」进入，P7.7） */
  openTaskEdit(taskId: string): void;
}

const EMPTY_FILTERS: MapFilters = {};

export const useUiStore = create<UiState>((set) => ({
  currentProjectId: null,
  setCurrentProjectId: (id) => set({ currentProjectId: id }),

  currentProjectMeta: EMPTY_PROJECT_META,
  setCurrentProjectMeta: (meta) =>
    set((state) => ({ currentProjectMeta: { ...state.currentProjectMeta, ...meta } })),
  clearCurrentProjectMeta: () => set({ currentProjectMeta: EMPTY_PROJECT_META }),

  currentTaskId: null,
  /* P6.2：打开详情抽屉时顺带关掉新建任务抽屉（两抽屉互斥） */
  openTask: (id) =>
    set({
      currentTaskId: id,
      drawerOpen: true,
      taskFormOpen: false,
      taskFormProjectId: null,
      taskFormEditId: null,
    }),
  closeTask: () => set({ currentTaskId: null, drawerOpen: false }),

  filters: EMPTY_FILTERS,
  setFilter: (key, value) =>
    set((state) => {
      const next: MapFilters = { ...state.filters };
      if (value === undefined || value === '') {
        delete next[key];
      } else {
        next[key] = value;
      }
      return { filters: next };
    }),
  resetFilters: () => set({ filters: EMPTY_FILTERS }),

  drawerOpen: false,

  /* P6.2：打开新建任务抽屉时顺带关掉详情抽屉（两抽屉互斥） */
  taskFormOpen: false,
  taskFormProjectId: null,
  openTaskForm: (projectId) =>
    set({
      taskFormOpen: true,
      taskFormProjectId: projectId ?? null,
      taskFormEditId: null,
      currentTaskId: null,
      drawerOpen: false,
    }),
  closeTaskForm: () =>
    set({ taskFormOpen: false, taskFormProjectId: null, taskFormEditId: null }),

  taskFormEditId: null,
  /* ⭐ P7.5 / P7.7：打开编辑任务抽屉 —— 顺带关掉详情抽屉（两抽屉互斥） */
  openTaskEdit: (taskId) =>
    set({
      taskFormOpen: true,
      taskFormEditId: taskId,
      taskFormProjectId: null,
      currentTaskId: null,
      drawerOpen: false,
    }),
}));
